import { useState } from "react";

import type {
  IAddPro014ItemsResponse,
  IAddPro014ItemsResult,
  ICatalogItem,
} from "@/@types/campaign";
import type { CampaignConflictItem } from "@/@types/campaign-conflict";
import type { IGradeTarget, IGradeTargetPayload } from "@/@types/campaign-grade";
import { useToast } from "@/components/ui/use-toast";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useAddPro017ListItemsMutation, useSearchItemsQuery } from "@/services/campaign";
import { useAddPro014CampaignItemsMutation } from "@/services/campaign-pro014";

import { CAMPAIGN_TYPES } from "../components/edit-campaign/constants";
import type { AddItemsState } from "../types";
import { toGradeTargetPayloads } from "../utils/grade-targets";
import { ADD_ITEM_TEXTS, TOAST_VARIANT } from "../utils/messages";
import { conflictsFromError, describeApiError } from "../utils/pro014-api-errors";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_LIMIT = 20;
const FIRST_LIST = "1";
const CODES_SEPARATOR = ", ";

const describePro014Added = (result: IAddPro014ItemsResult): string => {
  const skipped: string[] = result.skippedItemCodes ?? [];
  if (skipped.length === 0) return ADD_ITEM_TEXTS.SUCCESS(result.itemsAdded);
  return ADD_ITEM_TEXTS.SUCCESS_WITH_SKIPPED(result.itemsAdded, skipped.join(CODES_SEPARATOR));
};

interface UseAddItemsParams {
  campaignCode: number;
  campaignType: string;
  gradeRestricted: boolean;
  onAdded: () => void;
}

export function useAddItems({
  campaignCode,
  campaignType,
  gradeRestricted,
  onAdded,
}: UseAddItemsParams): AddItemsState {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<ICatalogItem[]>([]);
  const [selectedList, setSelectedList] = useState<string>(FIRST_LIST);
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [gradesBySku, setGradesBySku] = useState<Record<string, IGradeTarget[]>>({});
  const [gradeItem, setGradeItem] = useState<ICatalogItem | null>(null);

  const [addPro014Items, { isLoading: isAddingPro014 }] = useAddPro014CampaignItemsMutation();
  const [addPro017Items, { isLoading: isAddingPro017 }] = useAddPro017ListItemsMutation();

  const isPro017: boolean = campaignType.toUpperCase() === CAMPAIGN_TYPES.PRO017;
  const requiresGrade: boolean = gradeRestricted && !isPro017;
  const typedQuery: string = useDebouncedValue(searchQuery.trim(), SEARCH_DEBOUNCE_MS);
  const debouncedQuery: string = typedQuery.length >= MIN_SEARCH_LENGTH ? typedQuery : "";

  const { data: searchData, isFetching: isSearching } = useSearchItemsQuery(
    { q: debouncedQuery, limit: SEARCH_LIMIT },
    { skip: debouncedQuery.length < MIN_SEARCH_LENGTH },
  );

  const itemsWithoutGrade: string[] = requiresGrade
    ? selectedItems
        .filter((item: ICatalogItem) => (gradesBySku[item.code]?.length ?? 0) === 0)
        .map((item: ICatalogItem) => item.code)
    : [];

  const reset = (): void => {
    setSelectedItems([]);
    setSearchQuery("");
    setConflicts([]);
    setGradesBySku({});
    setGradeItem(null);
  };

  const toggleItem = (item: ICatalogItem): void =>
    setSelectedItems((previous: ICatalogItem[]) => {
      if (previous.some((selected) => selected.code === item.code)) {
        return previous.filter((selected) => selected.code !== item.code);
      }
      return [...previous, { code: item.code, name: item.name }];
    });

  const removeItem = (itemCode: string): void =>
    setSelectedItems((previous: ICatalogItem[]) =>
      previous.filter((selected: ICatalogItem) => selected.code !== itemCode),
    );

  const confirmGrades = (grades: IGradeTarget[]): void => {
    if (!gradeItem) return;
    setGradesBySku((previous) => ({ ...previous, [gradeItem.code]: grades }));
    setGradeItem(null);
  };

  const gradeTargetsOf = (itemCodes: string[]): IGradeTargetPayload[] | undefined => {
    if (!requiresGrade) return undefined;
    return itemCodes.flatMap((itemCode: string) =>
      toGradeTargetPayloads(itemCode, gradesBySku[itemCode]),
    );
  };

  const addSelected = async (itemCodes: string[]): Promise<string> => {
    if (isPro017) {
      const added: { data: { itemsAdded: number } } = await addPro017Items({
        code: campaignCode,
        listNumber: Number(selectedList),
        itemCodes,
      }).unwrap();
      return ADD_ITEM_TEXTS.SUCCESS(added.data.itemsAdded);
    }
    const response: IAddPro014ItemsResponse = await addPro014Items({
      code: campaignCode,
      itemCodes,
      gradeTargets: gradeTargetsOf(itemCodes),
    }).unwrap();
    return describePro014Added(response.data);
  };

  const add = async (): Promise<void> => {
    if (selectedItems.length === 0 || itemsWithoutGrade.length > 0) return;
    const itemCodes: string[] = selectedItems.map((item: ICatalogItem) => String(item.code));
    setConflicts([]);
    try {
      const description: string = await addSelected(itemCodes);
      toast({
        title: ADD_ITEM_TEXTS.SUCCESS_TITLE,
        description,
        variant: TOAST_VARIANT.DEFAULT,
      });
      reset();
      onAdded();
    } catch (error: unknown) {
      const found: CampaignConflictItem[] = conflictsFromError(error);
      if (found.length > 0) {
        setConflicts(found);
        return;
      }
      toast({
        title: ADD_ITEM_TEXTS.ERROR_TITLE,
        description: describeApiError(error, ADD_ITEM_TEXTS.ERROR),
        variant: TOAST_VARIANT.DESTRUCTIVE,
      });
    }
  };

  return {
    searchQuery,
    debouncedQuery,
    searchResults: searchData?.items,
    isSearching,
    selectedItems,
    selectedList,
    conflicts,
    gradesBySku,
    gradeItem,
    itemsWithoutGrade,
    canAdd: selectedItems.length > 0 && itemsWithoutGrade.length === 0,
    isAdding: isAddingPro014 || isAddingPro017,
    setSearchQuery,
    setSelectedList,
    isItemSelected: (itemCode: string) => selectedItems.some((item) => item.code === itemCode),
    toggleItem,
    removeItem,
    openGrade: setGradeItem,
    closeGrade: () => setGradeItem(null),
    confirmGrades,
    add,
    reset,
  };
}
